import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Search,
  UserPlus,
  Filter,
  MoreVertical,
  Eye,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { getLeads, deleteLead } from '../../lib/api';
import { formatDate } from '../../lib/utils';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  negotiation: 'bg-purple-100 text-purple-700',
  contracting: 'bg-indigo-100 text-indigo-700',
  on_hold: 'bg-slate-100 text-slate-700',
  closed_won: 'bg-green-100 text-green-700',
  closed_lost: 'bg-red-100 text-red-700',
  postponed: 'bg-orange-100 text-orange-700',
};

const SOURCE_LABELS = {
  facebook: 'Facebook',
  web: 'Website',
  google_ads: 'Google Ads',
  referral: 'Referral',
  cold_call: 'Cold Call',
  trade_show: 'Trade Show',
  csv_upload: 'CSV Upload',
  other: 'Other',
};

export default function LeadsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [statuses, setStatuses] = useState({});
  const [sources, setSources] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [sourceFilter, setSourceFilter] = useState(searchParams.get('source') || 'all');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '0'));
  const limit = 20;

  useEffect(() => {
    loadLeads();
  }, [statusFilter, sourceFilter, page]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (page === 0) {
        loadLeads();
      } else {
        setPage(0);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      const params = {
        limit,
        skip: page * limit,
      };
      if (search) params.search = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (sourceFilter && sourceFilter !== 'all') params.source = sourceFilter;

      const response = await getLeads(params);
      setLeads(response.data.leads);
      setTotal(response.data.total);
      setStatusCounts(response.data.status_counts || {});
      setStatuses(response.data.statuses || {});
      setSources(response.data.sources || []);
      setBusinessTypes(response.data.business_types || []);

      // Update URL params
      const newParams = new URLSearchParams();
      if (search) newParams.set('search', search);
      if (statusFilter && statusFilter !== 'all') newParams.set('status', statusFilter);
      if (sourceFilter && sourceFilter !== 'all') newParams.set('source', sourceFilter);
      if (page > 0) newParams.set('page', page.toString());
      setSearchParams(newParams);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLead = async (leadId, businessName) => {
    if (!window.confirm(`Are you sure you want to delete "${businessName}"?`)) return;
    try {
      await deleteLead(leadId);
      toast.success('Lead deleted');
      loadLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete lead');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="leads-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">All Leads</h1>
          <p className="text-slate-500">Manage and track your sales pipeline</p>
        </div>
        <Button asChild className="bg-indigo-600 hover:bg-indigo-700">
          <Link to="/sales/leads/new">
            <UserPlus className="w-4 h-4 mr-2" />
            Add Lead
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="lead-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(statuses).map(([key, value]) => (
              <SelectItem key={key} value={key}>
                {value.label} ({statusCounts[key] || 0})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-[180px]" data-testid="source-filter">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {sources.map((source) => (
              <SelectItem key={source} value={source}>
                {SOURCE_LABELS[source] || source}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Leads List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-6 w-48 mb-2" />
                <Skeleton className="h-4 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : leads.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No leads found</h3>
            <p className="text-slate-500 mb-4">
              {search || statusFilter !== 'all' || sourceFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Add your first lead to start building your pipeline'}
            </p>
            <Button asChild className="bg-indigo-600 hover:bg-indigo-700">
              <Link to="/sales/leads/new">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Lead
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {leads.map((lead) => (
              <Card 
                key={lead.lead_id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/sales/leads/${lead.lead_id}`)}
                data-testid={`lead-card-${lead.lead_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">{lead.business_name}</h3>
                        {lead.contact_name && (
                          <p className="text-slate-600">{lead.contact_name}</p>
                        )}
                        <div className="flex flex-wrap gap-3 mt-2">
                          <span className="flex items-center gap-1 text-sm text-slate-500">
                            <Phone className="w-3 h-3" />
                            {lead.phone}
                          </span>
                          {lead.email && (
                            <span className="flex items-center gap-1 text-sm text-slate-500">
                              <Mail className="w-3 h-3" />
                              {lead.email}
                            </span>
                          )}
                          {lead.location && (
                            <span className="flex items-center gap-1 text-sm text-slate-500">
                              <MapPin className="w-3 h-3" />
                              {lead.location}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-3">
                          <Badge className={STATUS_COLORS[lead.status]}>
                            {lead.status.replace('_', ' ')}
                          </Badge>
                          <Badge variant="outline" className="capitalize">
                            {lead.business_type?.replace('_', ' ')}
                          </Badge>
                          <span className="text-xs text-slate-400">
                            {SOURCE_LABELS[lead.source] || lead.source}
                          </span>
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(lead.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/sales/leads/${lead.lead_id}`); }}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        {lead.status !== 'closed_won' && (
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); handleDeleteLead(lead.lead_id, lead.business_name); }}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-slate-500">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} leads
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-600 px-2">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
